import { useState } from "react";
import { Play, Clock } from "lucide-react";

const VIDEO_ID = "QSFqpV0f8Ak";
const THUMBNAIL = `https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`;

export default function VideoTour() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="tour"
      data-testid="video-tour-section"
      className="relative py-24 md:py-32 bg-[#F4F1EA] overflow-hidden"
    >
      {/* soft ornamental accent */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 400px at 10% 10%, rgba(102,123,104,0.15), transparent 60%), radial-gradient(600px 400px at 95% 90%, rgba(166,124,82,0.12), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-16">
          <p
            data-testid="video-tour-eyebrow"
            className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-[#667B68]"
          >
            Video Tour
          </p>
          <h2
            data-testid="video-tour-title"
            className="reveal font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C2A24] mt-4"
            style={{ transitionDelay: "80ms" }}
          >
            A walk through
            <br className="hidden sm:block" /> our home.
          </h2>
          <p
            className="reveal mt-6 text-[17px] leading-relaxed text-[#3E574B] max-w-2xl"
            style={{ transitionDelay: "140ms" }}
          >
            Take a moment to step inside — see the rooms, the gardens, and the
            gentle rhythm of daily life at Carlene&rsquo;s.
          </p>
        </div>

        <div
          className="reveal relative max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(44,76,59,0.4)] bg-[#1C2A24] aspect-video"
          data-testid="video-tour-player"
          style={{ transitionDelay: "220ms" }}
        >
          {playing ? (
            <iframe
              title="Carlene's Comfort Care — Home Tour"
              src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-tour-iframe"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              data-testid="video-tour-play-btn"
              aria-label="Play home tour video"
              className="group absolute inset-0 w-full h-full focus:outline-none focus:ring-2 focus:ring-[#C9A24A]/70"
            >
              <img
                src={THUMBNAIL}
                alt="Carlene's Comfort Care — video tour preview"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                onError={(e) => {
                  // Fallback if maxres not available
                  e.currentTarget.src = `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`;
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40 transition-colors duration-500 group-hover:from-black/60" />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="relative inline-flex items-center justify-center">
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-white/30 blur-xl scale-125 group-hover:scale-150 transition-transform duration-500"
                  />
                  <span className="relative inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 text-[#2C4C3B] shadow-[0_12px_40px_rgba(0,0,0,0.35)] transition-all duration-300 group-hover:scale-110 group-hover:bg-white">
                    <Play className="w-7 h-7 sm:w-8 sm:h-8 fill-current translate-x-[2px]" />
                  </span>
                </span>
              </div>

              {/* Bottom caption */}
              <div className="absolute left-0 right-0 bottom-0 p-6 sm:p-8 flex items-end justify-between gap-4 text-white">
                <div className="text-left">
                  <p className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C9A24A]">
                    Play Tour
                  </p>
                  <p className="font-heading text-xl sm:text-2xl mt-1">
                    Inside Carlene&rsquo;s Comfort Care
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                  <Clock className="w-3.5 h-3.5" />
                  Short video
                </span>
              </div>
            </button>
          )}
        </div>

        <p className="reveal text-center text-[13px] text-[#667B68] mt-6" style={{ transitionDelay: "280ms" }}>
          Hosted on YouTube. Click the play button above to start the tour.
        </p>
      </div>
    </section>
  );
}
